import React from "react";

import { HiOutlinePlus } from "react-icons/hi";
import { FiTrash2 } from "react-icons/fi";
import { TimePicker, Button as ButtonLib } from "@npm_leadtech/qr-ui-components";
import { useTranslation } from "react-i18next";

import { Button } from "../Button";
import { Checkbox } from "../Checkbox";
import { Divider } from "../Divider";

import { parseTimeStringToTimeOfDayValue } from "@/utils/timerHelper";
import { NAMESPACES } from "@/internals/constants/namespaces";
import { PAGES } from "@/internals/constants/pages";

import {
  BUSINESS_HOURS_DAY_KEYS,
  BusinessHoursViewProps,
  BusinessHourTime,
  TimeRowProps,
} from "./types";
import { TimeOfDayValue } from "@/types/time";

const TimeRow = (props: TimeRowProps) => {
  const {
    day,
    timeIndex,
    timeFormat,
    isEnabled,
    value,
    fromLabel,
    toLabel,
    onAddTimeSlot,
    onRemoveTimeSlot,
    onTimeChange,
  } = props;

  const { t: timePickerTF } = useTranslation(NAMESPACES.TIME_PICKER);

  const isFirstItem = timeIndex === 0;

  return (
    <div
      key={`${day}-${timeIndex}`}
      className="opening-hours__days__day__times__time"
    >
      <div className="opening-hours__time-picker">
        <TimePicker
          dataQA={`business-hours-start-time-${day}-${timeIndex}`}
          ariaLabel="Start time"
          label={fromLabel}
          value={parseTimeStringToTimeOfDayValue(value?.start)}
          placeholder={timeFormat === "AM/PM" ? "08:00 am" : "08:00"}
          use12Hours={timeFormat === "AM/PM"}
          needConfirm={true}
          confirmText={timePickerTF("save_label")}
          disabled={!isEnabled}
          onChange={(timeValue?: TimeOfDayValue) =>
            onTimeChange(day, timeIndex, "start", timeValue)
          }
        />
      </div>
      <div className="opening-hours__time-picker">
        <TimePicker
          dataQA={`business-hours-end-time-${day}-${timeIndex}`}
          ariaLabel="End time"
          label={toLabel}
          value={parseTimeStringToTimeOfDayValue(value?.end)}
          placeholder={timeFormat === "AM/PM" ? "08:00 am" : "08:00"}
          use12Hours={timeFormat === "AM/PM"}
          needConfirm={true}
          confirmText={timePickerTF("save_label")}
          disabled={!isEnabled}
          onChange={(timeValue?: TimeOfDayValue) => onTimeChange(day, timeIndex, "end", timeValue)}
        />
      </div>
      <ButtonLib
        id="opening-hours__add-remove-button"
        shape="outlined"
        dataQA="business-hours-add-remove-button"
        variant={isFirstItem ? "primary" : "error"}
        iconLeft={isFirstItem ? <HiOutlinePlus /> : <FiTrash2 />}
        onClick={() => (isFirstItem ? onAddTimeSlot(day) : onRemoveTimeSlot(day, timeIndex))}
        disabled={!isEnabled}
        ariaLabel="Add or remove time slot"
      />
    </div>
  );
};

export const BusinessHoursView = (props: BusinessHoursViewProps) => {
  const {
    ctaStyle,
    timeFormat,
    days,
    onTimeFormatChange,
    onDayEnabledChange,
    onAddTimeSlot,
    onRemoveTimeSlot,
    onTimeChange,
  } = props;

  const { t: timePickerTF } = useTranslation(NAMESPACES.TIME_PICKER);
  const { t: businessTF } = useTranslation(PAGES.QR_BUSINESS);

  return (
    <div className="opening-hours">
      {/* TODO: These are not ButtonLib buttons and they should be part of another component of the library (a switch for example) */}
      <ul className="opening-hours__actions">
        <li className="opening-hours__actions-item">
          <Button
            type="button"
            label={timePickerTF("time_format_am_pm")}
            tertiary
            onClick={() => onTimeFormatChange("AM/PM")}
            className={`${timeFormat === "24" && "hours__format--disabled"}`}
            rounded={!!ctaStyle}
          />
        </li>
        <li className="opening-hours__actions-item">
          <Button
            type="button"
            label={timePickerTF("time_format_24")}
            tertiary
            onClick={() => onTimeFormatChange("24")}
            className={`${timeFormat === "AM/PM" && "hours__format--disabled"}`}
            rounded={!!ctaStyle}
          />
        </li>
      </ul>

      <div className="opening-hours__days">
        {BUSINESS_HOURS_DAY_KEYS.map((day) => {
          const dayData = days[day];
          const times = dayData.times.length ? dayData.times : [{ start: "", end: "" }];

          return (
            <div key={day}>
              <div className="opening-hours__days__day">
                <Checkbox
                  id={businessTF(`time_${day}`).toLowerCase()}
                  label={businessTF(`time_${day}`)}
                  defaultValue={dayData.enabled}
                  className="opening-hours__days__day__checkbox"
                  onChange={(e) => onDayEnabledChange(day, e.target.checked)}
                />
                <div className="opening-hours__days__day__times">
                  {times.map((time, idx) => (
                    <TimeRow
                      key={`${day}-${idx}`}
                      day={day}
                      timeIndex={idx}
                      timeFormat={timeFormat}
                      isEnabled={dayData.enabled}
                      value={time as BusinessHourTime}
                      fromLabel={timePickerTF("from_label")}
                      toLabel={timePickerTF("to_label")}
                      onAddTimeSlot={onAddTimeSlot}
                      onRemoveTimeSlot={onRemoveTimeSlot}
                      onTimeChange={onTimeChange}
                    />
                  ))}
                </div>
              </div>

              {day !== "sunday" && (
                <Divider
                  spacing={4}
                  className="opening-hours__days__divider"
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
